'use client'

import { useEffect, useState, useSyncExternalStore } from 'react'
import Bowser from 'bowser'
import { useExtracted, useLocale } from 'next-intl'
import { ArrowRight, Download } from '@/components/ui/icons'
import { AppleGlyph, WindowsGlyph } from '@/components/ui/platformGlyphs'
import { DESKTOP_DOWNLOADS, DESKTOP_PLATFORM_LABELS, type DesktopPlatform } from '@/lib/desktop-releases'

type DetectedOs = DesktopPlatform | 'unknown'
type UserAgentOs = 'mac' | 'windows' | 'unknown'

interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<{ architecture?: string }>
}

interface NavigatorWithUAData extends Navigator {
  userAgentData?: NavigatorUAData
}

function detectUserAgentOs(): UserAgentOs {
  if (typeof navigator === 'undefined') return 'unknown'
  const browser = Bowser.getParser(navigator.userAgent)
  const osName = browser.getOSName().toLowerCase()
  if (osName.includes('mac')) return 'mac'
  if (osName.includes('windows')) return 'windows'
  return 'unknown'
}

function detectOs(): DetectedOs {
  return detectUserAgentOs() === 'windows' ? 'windows' : 'unknown'
}

async function detectAppleSiliconMac(): Promise<boolean> {
  if (typeof navigator === 'undefined' || detectUserAgentOs() !== 'mac') return false

  const userAgentData = (navigator as NavigatorWithUAData).userAgentData
  if (!userAgentData?.getHighEntropyValues) return false

  const { architecture } = await userAgentData.getHighEntropyValues(['architecture'])
  return ['arm', 'arm64', 'aarch64'].includes((architecture ?? '').toLowerCase())
}

function useDetectedOs(): DetectedOs {
  const syncDetected = useSyncExternalStore<DetectedOs>(
    () => () => {},
    detectOs,
    () => 'unknown',
  )
  const [isAppleSiliconMac, setIsAppleSiliconMac] = useState(false)

  useEffect(() => {
    let cancelled = false

    detectAppleSiliconMac()
      .then(isSupportedMac => {
        if (!cancelled) setIsAppleSiliconMac(isSupportedMac)
      })
      .catch(() => {
        if (!cancelled) setIsAppleSiliconMac(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return isAppleSiliconMac ? 'macArm' : syncDetected
}

interface PlatformCard {
  platform: DesktopPlatform
  url: string
  fileFormat: string
  glyph: (props: { className?: string }) => React.JSX.Element
}

const PLATFORM_CARDS: PlatformCard[] = [
  {
    platform: 'macArm',
    url: DESKTOP_DOWNLOADS.macArm,
    fileFormat: '.dmg',
    glyph: AppleGlyph,
  },
  {
    platform: 'windows',
    url: DESKTOP_DOWNLOADS.windows,
    fileFormat: '.exe',
    glyph: WindowsGlyph,
  },
]

interface Props {
  releaseDate: string
  fileSizes: Record<DesktopPlatform, string>
}

export function DownloadClient({ releaseDate, fileSizes }: Props) {
  const t = useExtracted()
  const locale = useLocale()
  const detected = useDetectedOs()
  const releaseMonth = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(releaseDate))

  const heroLabel =
    detected === 'macArm'
      ? t('Download for Mac')
      : detected === 'windows'
        ? t('Download for Windows')
        : t('See all downloads')
  const heroHref = detected === 'unknown' ? '#all-platforms' : DESKTOP_DOWNLOADS[detected]
  const sentenceTerminator = locale === 'ja' ? '。' : '.'

  return (
    <div className="flex flex-col gap-20">
      {/* Hero */}
      <section className="flex flex-col items-center text-center">
        <h1 className="text-balance text-5xl font-semibold tracking-tight text-white sm:text-6xl">
          {t('Martini for desktop')}
        </h1>
        <p className="mt-6 max-w-xl text-balance text-base text-zinc-400 sm:text-lg">
          {t('The full app, in a window. Faster cold starts, native menus, and deep links.')}
        </p>

        <a
          href={heroHref}
          className="group mt-10 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]"
        >
          {heroLabel}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>

        <p className="mt-4 text-sm text-zinc-500">{t('Released {date}', { date: releaseMonth })}</p>
      </section>

      {/* All platforms */}
      <section id="all-platforms" className="flex flex-col gap-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">{t('All platforms')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PLATFORM_CARDS.map(card => {
            const isDetected = card.platform === detected
            const Glyph = card.glyph
            return (
              <a
                key={card.platform}
                href={card.url}
                className={`group flex flex-col gap-5 rounded-2xl border bg-zinc-950/50 p-6 transition-all duration-200 hover:border-zinc-700 hover:bg-zinc-900/60 ${
                  isDetected ? 'border-white/30' : 'border-zinc-800/60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <Glyph className="h-8 w-8 text-white" />
                  {isDetected && (
                    <span className="rounded-full border border-white/20 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/80">
                      {t('Detected')}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-white">{DESKTOP_PLATFORM_LABELS[card.platform]}</p>
                  <p className="mt-1 text-sm text-zinc-500">
                    {card.fileFormat} · {fileSizes[card.platform]}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-300 transition-colors group-hover:text-white">
                  <Download className="h-4 w-4" />
                  {t('Download')}
                </div>
              </a>
            )
          })}
        </div>
      </section>

      {/* System requirements */}
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">{t('System requirements')}</h2>
        <ul className="grid grid-cols-1 gap-2 text-sm text-zinc-400 sm:grid-cols-2">
          <li>{t('macOS 12 Monterey or later (Apple Silicon)')}</li>
          <li>{t('Windows 10 or later (x64)')}</li>
          <li>{t('4 GB RAM recommended')}</li>
          <li>{t('Stable internet connection')}</li>
        </ul>
      </section>

      {/* Already installed */}
      <section className="border-t border-zinc-800/30 pt-10">
        <p className="text-sm text-zinc-500">
          {t('Already installed?')}{' '}
          <a href="martini://projects" className="text-zinc-300 underline-offset-4 hover:text-white hover:underline">
            {t('Open the app')}
          </a>
          {sentenceTerminator}
        </p>
      </section>
    </div>
  )
}
